import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const OG_IMAGE = `https://elpajardebenito.es${hero1280Asset.url}`;
const URL_CANON = "https://elpajardebenito.es/casa-rural-cerca-madrid";

const faqs = [
  { q: "¿A cuánto está de Madrid?", a: "El Ordial (Guadalajara) está a 1 h 30 min de Madrid por la A-2 y la CM-1101. Una escapada rural corta, ideal para desconectar el fin de semana." },
  { q: "¿Se puede reservar solo una noche?", a: "Sí, aceptamos estancias de una noche fuera de temporada alta. En puentes y festivos aplicamos estancia mínima de 2 noches." },
  { q: "¿Qué se puede hacer en los alrededores?", a: "Senderismo al Pico Alto Rey, ruta por los pueblos negros (Valverde, Majaelrayo, Campillo), Hayedo de Tejera Negra y observación de estrellas." },
  { q: "¿La casa dispone de calefacción y chimenea?", a: "Sí. La casa cuenta con calefacción central y chimenea de leña en el salón, perfecta para escapadas de invierno cerca de Madrid." },
];

export const Route = createFileRoute("/casa-rural-cerca-madrid")({
  head: () => ({
    meta: [
      { title: "Casa rural cerca de Madrid a 1 h 30 · El Pajar de Benito" },
      { name: "description", content: "Casa rural cerca de Madrid, en la Sierra Norte de Guadalajara. Chimenea, jardín y hasta 14 plazas. Escapada rural a 1 h 30 por la A-2." },
      { property: "og:title", content: "Casa rural cerca de Madrid · Sierra Norte de Guadalajara" },
      { property: "og:description", content: "Escapada rural a 1 h 30 min de Madrid: chimenea, jardín, pueblos negros y cielos estrellados." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Casa rural cerca de Madrid", to: "/casa-rural-cerca-madrid" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Casa rural cerca de Madrid" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Casa rural cerca de Madrid" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Escapada rural</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural cerca de Madrid</h1>
          <p className="mt-4 max-w-2xl text-white/90">A solo 1 h 30 min por la A-2. Sierra Norte de Guadalajara, chimenea y naturaleza para desconectar de verdad.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">La escapada rural más cercana a Madrid</h2>
        <p className="mt-4 text-muted-foreground">Cuando piensas en una <strong>casa rural cerca de Madrid</strong>, imaginas naturaleza, chimenea y silencio. Eso es <strong>El Pajar de Benito</strong>: una casa rural completa en El Ordial (Guadalajara), a 1 h 30 min por la A-2, en plena Sierra del Alto Rey.</p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { t: "1 h 30 de Madrid", d: "Ruta directa por A-2 hasta Jadraque y CM-1101." },
            { t: "Hasta 14 plazas", d: "Casa completa o por habitaciones dobles con baño." },
            { t: "Chimenea y jardín", d: "Salón con chimenea de leña y jardín privado." },
            { t: "Naturaleza", d: "Pico Alto Rey, Hayedo de Tejera Negra y pueblos negros." },
            { t: "Cielos oscuros", d: "Zona de baja contaminación lumínica: Vía Láctea a simple vista." },
            { t: "Reserva directa", d: "Sin comisiones · trato personal." },
          ].map((b) => (
            <div key={b.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Cómo llegar desde Madrid</h2>
          <ol className="mt-6 list-decimal space-y-2 pl-6 text-muted-foreground">
            <li>Salir de Madrid por la <strong>A-2 dirección Zaragoza</strong>.</li>
            <li>Tomar la salida hacia Jadraque (aprox. km 92) y seguir la <strong>CM-1101</strong>.</li>
            <li>Continuar por Hiendelaencina hasta El Ordial. <strong>Tiempo total: 1 h 30 min</strong> (≈ 130 km).</li>
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["casa-rural-grupos-cerca-madrid", "arquitectura-negra", "guia-hayedo-tejera-negra"]} />

      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Reserva tu escapada</h2>
        <p className="mt-4 text-muted-foreground">Comprueba fechas y precios en tiempo real.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/entorno" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Qué ver</Link>
        </div>
      </section>
    </>
  );
}